/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import ItemsSelectionMode from '@/components/batches/ItemsSelectionMode'
import UploadCsvForm from '@/components/batches/UploadCsvForm'
import {
  registerCSVAssetsLicensesBatch,
  registerAssetsLicensesBatch
} from '@/services/assets-licenses-batches'
import { setItemCustomProps, getItemPropByTable, tableColumns } from './handlers'
import { getListedAssets } from '@/services/listed-assets'
import {
  ASSETS_LICENSING_BATCH_CSV_TITLE,
  CREATE_ASSET_LICENSING_BATCH,
  CREATE_ASSET_LICENSING_BATCH_DESCRIPTION,
  RETURN_TO_LICENSE_MULTIPLE_LISTED_ASSETS
} from '@/configs/strings/asset-licenses-batches.strings'
import { assetLicenseBatchesUrls } from '@/configs/urls'
import ItemBatchCreator from '@/components/forms/predefined/ItemBatchCreator'
import { filterOptions, sortOptions } from '@/pages/AssetLicenses/pages/AssetLicensesList/components/AssetListingsToggle/handlers'
import HorizontalCardWithAddRemoveOptions from '@/components/cards/HorizontalCardWithAddRemoveOptions'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { defaultToastConfig } from '@/configs/toast'

const CreateAssetLicensesBatch = () => {
  const [uploadingFile, setUploadingFile] = useState(false)
  const toast = useToast()
  const [itemsSelectionMode, setItemsSelectionMode] = useState('uploadCsv')
  const isUploadCsv = itemsSelectionMode === 'uploadCsv'
  const navigate = useNavigate()
  const onSubmitCSV = async (formData, getAccessToken) => {
    setUploadingFile(true)
    return await registerCSVAssetsLicensesBatch(formData, getAccessToken)
  }
  const uploadFileResponse = async (response) => {
    try {
      toast({
        title: ASSETS_LICENSING_BATCH_CSV_TITLE,
        description: `The ${ASSETS_LICENSING_BATCH_CSV_TITLE} '${response.name}' is being processed; we will notify you when it is finished.`,
        status: 'success',
        ...defaultToastConfig
      })

      if (response?.id) {
        setUploadingFile(false)

        navigate(`/${assetLicenseBatchesUrls.base}/${response.id}`, { replace: true })
      }
    } catch (error) {
      toast({
        title: `Error on ${ASSETS_LICENSING_BATCH_CSV_TITLE}`,
        description: error.response?.data?.message ||
          `Failed processing the ${ASSETS_LICENSING_BATCH_CSV_TITLE}. Please contact with us.`,
        status: 'error',
        ...defaultToastConfig
      })
    }
  }

  return (
    <RegularTemplate
      backLabel={RETURN_TO_LICENSE_MULTIPLE_LISTED_ASSETS}
      backUrl={`/${assetLicenseBatchesUrls.base}`}
      description={CREATE_ASSET_LICENSING_BATCH_DESCRIPTION}
      title={CREATE_ASSET_LICENSING_BATCH}
      loadingContent={uploadingFile}
    >
      <ItemsSelectionMode
        className="mb-5 inline-block"
        onChange={setItemsSelectionMode}
      />
      { isUploadCsv
        ? <UploadCsvForm
            csvService={onSubmitCSV}
            getResponse={uploadFileResponse}
          />
        : <ItemBatchCreator
            baseUrl={assetLicenseBatchesUrls.base}
            filterOptions={filterOptions}
            getItemPropByTable={getItemPropByTable}
            getItemsService={getListedAssets}
            itemComponent={HorizontalCardWithAddRemoveOptions}
            itemsKey="assets"
            registerBatchService={registerAssetsLicensesBatch}
            searchResultsKeyData="data"
            searchResultsKeyLabel="name"
            searchResultsKeyValue="name"
            setItemCustomProps={setItemCustomProps}
            sortOptions={sortOptions}
            tableColumns={tableColumns}
          />
      }
    </RegularTemplate>
  )
}

export default CreateAssetLicensesBatch
